import type { Element } from '@borgar/simple-xml';
import type { Color, Theme } from '@jsfkit/types';
import { PRESET_COLORS, SYSTEM_COLORS } from '@jsfkit/utils';
import { attr, numAttr } from '../utils/attr.ts';
import { SCHEME_COLORS, INDEX_TO_SCHEME } from '../constants.ts';
import { readDrawingMLColor } from './readDrawingMLColor.ts';

/**
 * Reads a color element and returns a Color container. This should handle any of the
 * multiple color elements found within an XSLX workbook:
 *
 * Color types found in (workbook) styles:
 * - `<color>`
 * - `<fgColor>`
 * - `<bgColor>`
 *
 * Color types found in DrawingML:
 * - `<schemeClr>`
 * - `<hslClr>`
 * - `<prstClr>`
 * - `<scrgbClr>`
 * - `<srgbClr>`
 * - `<sysClr>`
 */
export function readColor (elm: Element | undefined | null, theme: Theme): Color | undefined {
  if (!elm) { return; }
  const tagName = elm.tagName;
  // §3.8.3 - bgColor
  // §3.8.18 - fgColor
  // §3.3.1.14 - color
  if (tagName === 'color' || tagName === 'fgColor' || tagName === 'bgColor') {
    // Element may have any of the following attributes:
    // - [auto]    - A boolean value indicating the color is automatic and system color dependent.
    // - [indexed] - References a color in indexedColors.
    // - [rgb]     - Standard Alpha Red Green Blue color value (ARGB).
    // - [theme]   - Index into the <clrScheme> collection, referencing a particular <sysClr> or
    //               <srgbClr> value expressed in the Theme part.
    // As well as:
    // - [tint]    - Specifies the tint value applied to the color (-1.0 .. 1.0)
    const auto = attr(elm, 'auto');
    if (auto === '1' || auto === 'true') {
      return { type: 'auto' };
    }

    let color: Color | undefined;
    const argb = attr(elm, 'rgb', ''); // ARGB
    if (argb) {
      // Convert ARGB to 6-digit sRGB hex. If the alpha byte is not fully opaque, preserve it as an
      // alpha transform since SrgbColor has no alpha field.
      const hex = argb.length === 8 ? argb.slice(2) : argb;
      color = { type: 'srgb', value: hex.toUpperCase() };
      if (argb.length === 8) {
        // NOTE: Yes, Excel removes this when it opens workbooks that have it, but we don't
        // have tested confirmation yet if this is [always] safe to remove, so we preserve it
        // leaving it up to consumers to discard the alpha channel at render-time.
        const alphaPct = parseInt(argb.slice(0, 2), 16) / 255 * 100;
        if (alphaPct < 100) {
          color.transforms = [ { type: 'alpha', value: alphaPct } ];
        }
      }
    }
    else {
      const indexed = attr(elm, 'indexed', '');
      if (indexed) {
        color = { type: 'indexed', value: +indexed };
      }
      else {
        // theme: A zero-based index into the <clrScheme> collection (§20.1.6.2),
        //        referencing a particular <sysClr> or <srgbClr> value expressed
        //        in the Theme part.
        const themeIdx = attr(elm, 'theme');
        if (themeIdx && theme) {
          const key = INDEX_TO_SCHEME[+themeIdx];
          if (key) {
            color = { type: 'theme', value: key } as Color;
          }
        }
      }
    }

    if (!color) {
      return;
    }

    // Convert the XLSX tint attribute to a shade or tint colour transform. XLSX tint is the
    // proportion shifted towards white (positive) or black (negative), but the internal
    // ColorTransform value uses the DrawingML convention: the percentage of the original
    // colour to retain. So XLSX tint=0.4 ("add 40% white") becomes value=60 ("retain 60%").
    const tint = numAttr(elm, 'tint', 0);
    if (tint < 0) {
      color.transforms = [ { type: 'shade', value: (1 + tint) * 100 } ];
    }
    else if (tint > 0) {
      color.transforms = [ { type: 'tint', value: (1 - tint) * 100 } ];
    }

    return color;
  }

  // DrawingML colour elements — validate, then delegate to readDrawingMLColor()

  // §5.1.2.2.29: Scheme Color - "specifies a color bound to a user's theme"
  else if (tagName === 'schemeClr') {
    const val = attr(elm, 'val');
    if (val && val in SCHEME_COLORS) {
      const jsfColor = readDrawingMLColor(elm);
      if (jsfColor) {
        return jsfColor;
      }
    }
  }

  // §5.1.2.2.13: HSL Color Model - "a perceptual gamma of 2.2 is assumed"
  // §5.1.2.2.32: RGB Color Model - Hex Variant
  // §5.1.2.2.30: RGB Color Model - Percentage Variant
  else if (tagName === 'hslClr' || tagName === 'srgbClr' || tagName === 'scrgbClr') {
    const jsfColor = readDrawingMLColor(elm);
    if (jsfColor) {
      return jsfColor;
    }
  }

  // §5.1.2.2.22: Preset Color
  else if (tagName === 'prstClr') {
    const val = attr(elm, 'val');
    if (val && val in PRESET_COLORS) {
      const jsfColor = readDrawingMLColor(elm);
      if (jsfColor) {
        return jsfColor;
      }
    }
  }

  // §5.1.2.2.33: System Color
  else if (tagName === 'sysClr') {
    // §5.1.12.58: Specifies the system color value
    const val = attr(elm, 'val');
    if (val && SYSTEM_COLORS[val.toLowerCase()]) {
      // FIXME: JSF Color has no equivalent of sysClr's lastClr (§5.1.12.28, specifies the colour
      // value that was last computed by the generating application).
      const jsfColor = readDrawingMLColor(elm);
      if (jsfColor) {
        return jsfColor;
      }
    }
  }
  else if (tagName) {
    throw new Error('Unknown color element: ' + tagName);
  }
}
